import { inject, Injectable, isDevMode, PLATFORM_ID } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { HttpClient } from '@angular/common/http';
import { catchError, of, Observable } from 'rxjs';
import { isPlatformServer } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/core';

/**
 * Path to translation files in the built application
 * NOTE: Update this if you rename the app folder from 'invest_01_web_v2'
 */
const TRANSLATION_DIR = 'dist/apps/invest_01_web_v2/browser/assets/i18n';

/**
 * Dev-server fallback: `nx serve` never populates TRANSLATION_DIR (that's a
 * production-build-only output), so SSR reads straight from source instead.
 */
const DEV_TRANSLATION_DIR = 'apps/invest_01_web_v2/src/assets/i18n';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private transferState = inject(TransferState);

  getTranslation(lang: string): Observable<Translation> {
    const key = makeStateKey<Translation>(`transloco-${lang}`);

    // Server (SSR): Load translations from file system for SEO.
    // HTTP requests during SSR would trigger SSRF protection and fail.
    // TransferState passes data to client (no duplicate requests).
    if (isPlatformServer(this.platformId)) {
      try {
        const { existsSync, readFileSync } = require('fs');
        const { join } = require('path');

        const distPath = join(process.cwd(), TRANSLATION_DIR, `${lang}.json`);
        const filePath =
          isDevMode() && !existsSync(distPath)
            ? join(process.cwd(), DEV_TRANSLATION_DIR, `${lang}.json`)
            : distPath;
        const translation = JSON.parse(readFileSync(filePath, 'utf8'));

        this.transferState.set(key, translation);
        return of(translation);
      } catch (error) {
        console.warn(
          `Failed to load translation file for language: ${lang} during SSR.`,
          error
        );
        return of({});
      }
    }

    // Client: Use transferred translation or load via HTTP with version
    const cachedTranslation = this.transferState.get(key, null);
    if (cachedTranslation) {
      return of(cachedTranslation);
    }

    return this.http
      .get<Translation>(`/assets/i18n/${lang}.json?v=__BUILD_VERSION__`)
      .pipe(
        catchError(error => {
          console.warn(
            `Failed to load translation file for language: ${lang}.`,
            error
          );
          return of({});
        })
      );
  }
}
